import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Receipt, ArrowClockwise, MagnifyingGlass, TrendUp, TrendDown, Wallet, DownloadSimple,
} from "@phosphor-icons/react";
import DateRangePicker, { rangeToParams } from "@/components/DateRangePicker";
import { downloadCsv } from "@/lib/csv";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const last30 = () => {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from, to };
};

/**
 * scope="mine"      → Billing History (reseller ↔ admin)
 * scope="customers" → Payment History (customers ↔ reseller)
 */
export default function ResellerHistory({ scope = "mine" }) {
  const isMine = scope === "mine";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ added_minor: 0, deducted_minor: 0, net_minor: 0 });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [range, setRange] = useState(last30);
  const [q, setQ] = useState("");

  const sourceLabels = isMine
    ? {
      request: "Payment request",
      manual: "Admin adjustment",
      white_label: "White labeling",
      call_limit: "Call-limit purchase",
      ai_model: "AI Model",
    }
    : {
      request: "Customer request",
      manual: "Adjustment",
      call_limit: "Call-limit purchase",
      ai_model: "AI Model",
    };

  const sourceBadgeClass = (src) => {
    if (src === "request") return "bg-primary/10 text-primary border-primary/20";
    if (src === "white_label") return "bg-violet-500/10 text-violet-700 border-violet-500/20";
    if (src === "call_limit") return "bg-teal-500/10 text-teal-700 border-teal-500/20";
    if (src === "ai_model") return "bg-amber-500/10 text-amber-800 border-amber-500/20";
    return "bg-muted text-muted-foreground border-border";
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope, ...rangeToParams(range) };
      if (source !== "all") params.source = source;
      const { data } = await api.get("/billing/reseller-history", { params });
      setRows(data?.rows || []);
      setSummary(data?.summary || { added_minor: 0, deducted_minor: 0, net_minor: 0 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [scope, source, range]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.account_name, r.account_company, r.account_email,
        r.description, r.request_note, r.processed_by_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const cards = useMemo(() => {
    if (!q.trim()) return summary;
    let added = 0, deducted = 0;
    filtered.forEach((r) => {
      const a = r.amount_minor || 0;
      if (a >= 0) added += a; else deducted += -a;
    });
    return { added_minor: added, deducted_minor: deducted, net_minor: added - deducted };
  }, [filtered, summary, q]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const cols = [
      { label: "Date", value: (r) => fmtDate(r.approved_at || r.created_at) },
      ...(!isMine ? [
        { label: "Customer", value: (r) => r.account_name || "" },
        { label: "Email", value: (r) => r.account_email || "" },
      ] : []),
      { label: "Source", value: (r) => sourceLabels[r.source] || r.source || "" },
      { label: "Description", value: (r) => r.description || r.request_note || "" },
      { label: "Processed by", value: (r) => r.processed_by_name || "" },
      { label: "Amount (cents)", value: (r) => r.amount_minor ?? "" },
      { label: "Balance after (cents)", value: (r) => r.balance_after_minor ?? "" },
    ];
    downloadCsv(isMine ? "billing-history.csv" : "payment-history.csv", cols, filtered);
  };

  return (
    <div data-testid={`reseller-history-${scope}`}>
      <PageHeader
        title={isMine ? "Billing History" : "Customer payments"}
        subtitle={isMine
          ? "Payments, adjustments, white labeling, call-limit, and AI Model on your reseller account with the platform admin."
          : "Customer payments, adjustments, call-limit purchases, and AI Model charges (including Test Call)."}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-lg" onClick={exportCsv} disabled={!filtered.length}
            data-testid="reseller-history-export">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> Export CSV
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={load} data-testid="reseller-history-refresh">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="reseller-history-added" icon={TrendUp} tone="success"
          label="Added" value={loading ? "…" : money(cards.added_minor || 0)}
          hint="Credits in selected period" />
        <StatCard testid="reseller-history-deducted" icon={TrendDown} tone="warning"
          label="Deducted" value={loading ? "…" : money(cards.deducted_minor || 0)}
          hint="Debits in selected period" />
        <StatCard testid="reseller-history-net" icon={Wallet} tone="primary"
          label="Total (net)" value={loading ? "…" : money(cards.net_minor || 0)}
          hint="Added − deducted" />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1.5 min-w-[220px] flex-1 sm:flex-none sm:w-[260px]">
          <p className="text-xs text-muted-foreground">Date range</p>
          <DateRangePicker value={range} onChange={(r) => setRange(r?.from ? r : last30())}
            testid="reseller-history-dates" />
        </div>
        <div className="space-y-1.5 min-w-[160px]">
          <p className="text-xs text-muted-foreground">Source</p>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="reseller-history-source" className="rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="request">{isMine ? "Payment request" : "Customer request"}</SelectItem>
              <SelectItem value="manual">{isMine ? "Admin adjustment" : "Adjustment"}</SelectItem>
              {isMine && <SelectItem value="white_label">White labeling</SelectItem>}
              <SelectItem value="call_limit">Call-limit purchase</SelectItem>
              <SelectItem value="ai_model">AI Model</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <p className="text-xs text-muted-foreground">Search</p>
          <div className="relative">
            <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="reseller-history-search" className="pl-9 rounded-lg"
              placeholder={isMine ? "Description, note…" : "Customer, email, note…"}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          testid="reseller-history-empty"
          icon={Receipt}
          title={isMine ? "No billing history" : "No customer payments"}
          description={isMine
            ? "Entries appear when you submit a payment request that is approved, when admin adjusts your balance, or when you buy call-limit channels."
            : "Entries appear for customer payment requests, balance adjustments, call-limit purchases, and AI Model usage (live calls and Test Call)."}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="reseller-history-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                {!isMine && <TableHead>Customer</TableHead>}
                <TableHead>Source</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Processed by</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const credit = r.amount_minor >= 0;
                return (
                  <TableRow key={r.id} data-testid={`reseller-history-row-${r.id}`}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.approved_at || r.created_at)}
                    </TableCell>
                    {!isMine && (
                      <TableCell>
                        <div className="font-medium">{r.account_name || "—"}</div>
                        {r.account_email && (
                          <div className="text-xs text-muted-foreground">{r.account_email}</div>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-sm">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${sourceBadgeClass(r.source)}`}>
                        {sourceLabels[r.source] || r.source}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate"
                      title={[r.description, r.request_note].filter(Boolean).join(" — ")}>
                      {r.description || r.request_note || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.processed_by_name || "—"}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${credit ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                      {credit ? "+" : "−"}{money(Math.abs(r.amount_minor || 0))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.balance_after_minor || 0)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function ResellerBillingHistory() {
  return <ResellerHistory scope="mine" />;
}

export function ResellerPaymentHistory() {
  return <ResellerHistory scope="customers" />;
}
